import React, { useState } from "react";
import { Header } from "../../../../components";
import { Input, Button, message, Card, Tag, Popconfirm, Alert } from "antd";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTelegramStatus,
  useTelegramSendCode,
  useTelegramSignIn,
  useTelegramVerify,
  useTelegramLogout,
} from "../../../../hooks/useTelegram";

function TelegramPage() {
  const queryClient = useQueryClient();
  const { data: status = { linked: false }, isLoading: isLoadingStatus } = useTelegramStatus();
  const { mutateAsync: sendCode, isLoading: isSendingCode } = useTelegramSendCode();
  const { mutateAsync: signIn, isLoading: isSigningIn } = useTelegramSignIn();
  const { mutateAsync: verify, isLoading: isVerifying } = useTelegramVerify();
  const { mutateAsync: logout, isLoading: isLoggingOut } = useTelegramLogout();

  // step: "phone" -> กรอกเบอร์ | "code" -> กรอก OTP (+ 2FA ถ้าต้อง)
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [loginId, setLoginId] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [need2FA, setNeed2FA] = useState(false);

  const refreshStatus = () => queryClient.invalidateQueries(["telegramStatus"]);

  const resetFlow = () => {
    setStep("phone");
    setLoginId("");
    setCode("");
    setPassword("");
    setNeed2FA(false);
  };

  const handleSendCode = async () => {
    const p = phone.trim();
    if (!/^\+?[0-9]{8,15}$/.test(p)) {
      message.warning("กรุณากรอกเบอร์ให้ถูกต้อง เช่น 0812345678");
      return;
    }
    try {
      const res = await sendCode(p);
      setLoginId(res?.result?.loginId || "");
      setNeed2FA(false);
      setCode("");
      setPassword("");
      setStep("code");
      message.success("ส่ง OTP แล้ว กรุณาเช็คใน Telegram");
    } catch (error) {
      message.error(error?.response?.data?.message || "ส่ง OTP ไม่สำเร็จ");
    }
  };

  const handleSignIn = async () => {
    if (!code.trim()) {
      message.warning("กรุณากรอกรหัส OTP");
      return;
    }
    if (need2FA && !password) {
      message.warning("กรุณากรอกรหัสผ่าน 2FA");
      return;
    }
    try {
      const res = await signIn({ loginId, code: code.trim(), password });
      if (res?.result?.need2FA) {
        // บัญชีเปิด 2FA — โชว์ช่องรหัสผ่านแล้วให้กดยืนยันอีกครั้ง
        setNeed2FA(true);
        message.info("บัญชีนี้เปิด 2FA กรุณากรอกรหัสผ่าน");
        return;
      }
      message.success("ผูกบัญชี Telegram สำเร็จ");
      resetFlow();
      refreshStatus();
    } catch (error) {
      message.error(error?.response?.data?.message || "ยืนยัน OTP ไม่สำเร็จ");
    }
  };

  const handleVerify = async () => {
    try {
      const res = await verify();
      const r = res?.result || {};
      if (r.alive) {
        message.success("เชื่อมต่อปกติ ✓");
      } else if (r.expired) {
        message.error("บัญชีหลุดการเชื่อมต่อแล้ว กรุณาผูกใหม่");
        refreshStatus(); // row ถูกลบ → กลับไปหน้าผูกบัญชี
      } else if (r.unverified) {
        message.warning("ตรวจสอบไม่ได้ชั่วคราว (เครือข่ายอาจมีปัญหา) ลองใหม่อีกครั้ง");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "ตรวจสอบการเชื่อมต่อไม่สำเร็จ");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      message.success("ยกเลิกการผูกบัญชีแล้ว");
      resetFlow();
      refreshStatus();
    } catch (error) {
      message.error(error?.response?.data?.message || "ยกเลิกการผูกไม่สำเร็จ");
    }
  };

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-6">
        <Header category="Workspace" title="Telegram" />
      </div>

      <div style={{ maxWidth: 460 }}>
        {/* ผูกแล้ว */}
        {status.linked ? (
          <Card title="บัญชี Telegram ที่ผูกไว้" loading={isLoadingStatus}>
            <p style={{ marginBottom: 8 }}>
              <Tag color="green">เชื่อมต่อแล้ว</Tag>
            </p>
            <p><strong>ชื่อ:</strong> {status.name || "-"}</p>
            <p><strong>Username:</strong> {status.username ? `@${status.username}` : "-"}</p>
            <p><strong>เบอร์:</strong> {status.phone || "-"}</p>
            <p style={{ color: "#999", fontSize: 12 }}>
              ผูกเมื่อ: {status.linkedAt ? dayjs(status.linkedAt).format("DD/MM/YYYY HH:mm") : "-"}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button onClick={handleVerify} loading={isVerifying}>
                ตรวจสอบการเชื่อมต่อ
              </Button>
              <Popconfirm
                title="ยกเลิกการผูกบัญชีนี้?"
                onConfirm={handleLogout}
                okText="ยกเลิกผูก"
                cancelText="ปิด"
                okButtonProps={{ danger: true }}
              >
                <Button danger loading={isLoggingOut}>
                  ยกเลิกการผูกบัญชี
                </Button>
              </Popconfirm>
            </div>
          </Card>
        ) : (
          // ยังไม่ผูก — flow login
          <Card title="ผูกบัญชี Telegram" loading={isLoadingStatus}>
            {step === "phone" && (
              <>
                <p style={{ marginBottom: 8 }}>เบอร์โทรศัพท์</p>
                <Input
                  placeholder="0812345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onPressEnter={handleSendCode}
                  disabled={isSendingCode}
                />
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 16 }}
                  loading={isSendingCode}
                  onClick={handleSendCode}
                >
                  ส่ง OTP
                </Button>
              </>
            )}

            {step === "code" && (
              <>
                <Alert
                  type="info"
                  message={`ส่ง OTP ไปที่ ${phone} แล้ว — เปิดแอป Telegram เพื่อดูรหัส`}
                  style={{ marginBottom: 16 }}
                />
                <p style={{ marginBottom: 8 }}>รหัส OTP</p>
                <Input
                  placeholder="12345"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={isSigningIn}
                  autoFocus
                />

                {need2FA && (
                  <>
                    <p style={{ margin: "16px 0 8px" }}>รหัสผ่าน 2FA (Two-Step Verification)</p>
                    <Input.Password
                      placeholder="รหัสผ่าน 2FA"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onPressEnter={handleSignIn}
                      disabled={isSigningIn}
                    />
                  </>
                )}

                <Button
                  type="primary"
                  block
                  style={{ marginTop: 16 }}
                  loading={isSigningIn}
                  onClick={handleSignIn}
                >
                  ยืนยัน
                </Button>
                <Button type="link" block onClick={resetFlow} disabled={isSigningIn}>
                  เปลี่ยนเบอร์ / ขอ OTP ใหม่
                </Button>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

export default TelegramPage;
